import React, { useState, useEffect, useCallback } from 'react';
import './ReportsPage.css';

const ReportsPage = ({ user, isAdmin }) => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasonModal, setReasonModal] = useState({ open: false, text: '', requestId: null });
  const [filters, setFilters] = useState({
    status: '',
    customerEmail: isAdmin ? '' : user.email,
    fromDate: '',
    toDate: ''
  });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const url = isAdmin
        ? '/api/cd-requests/list'
        : `/api/cd-requests/my-requests/${encodeURIComponent(user.email)}`;

      const response = await fetch(url);
      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user.email]);

  const applyFilters = useCallback(() => {
    if (!Array.isArray(requests)) {
      setFilteredRequests([]);
      return;
    }

    let filtered = [...requests];

    if (filters.status) {
      filtered = filtered.filter(req => req.cd_request_status === filters.status);
    }

    if (filters.customerEmail && isAdmin) {
      filtered = filtered.filter(req =>
        req.customer_email.toLowerCase().includes(filters.customerEmail.toLowerCase())
      );
    }

    if (filters.fromDate) {
      filtered = filtered.filter(req =>
        new Date(req.creation_date) >= new Date(filters.fromDate)
      );
    }

    if (filters.toDate) {
      filtered = filtered.filter(req =>
        new Date(req.creation_date) <= new Date(filters.toDate)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, filters, isAdmin]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      customerEmail: isAdmin ? '' : user.email,
      fromDate: '',
      toDate: ''
    });
  };

  /**
   * Neutralises CSV formula injection (OWASP CSV Injection).
   * Any cell value beginning with a spreadsheet formula trigger character
   * ( = + - @ TAB CR ) is prefixed with a tab so that spreadsheet software
   * treats it as plain text instead of executing it as a formula.
   * Embedded double-quotes are also escaped by doubling them so they do not
   * break the RFC-4180 quoted-field structure.
   */
  const sanitizeCsvCell = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    const str = String(value);
    const neutralized = /^[=+\-@\t\r]/.test(str) ? `\t${str}` : str;
    return neutralized.replace(/"/g, '""');
  };

  const exportToCSV = () => {
    if (filteredRequests.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = [
      'CD Request',
      'Customer Name',
      'Customer Email',
      'Scan Length',
      'Margin Per Side',
      'INC',
      'MLS',
      'Pattern Length',
      'Count',
      'MLS CPR',
      'Start Position',
      'End Position',
      'Status',
      'Approver',
      'Rejection Reason',
      'Creation Date',
      'Created By'
    ];

    const csvData = filteredRequests.map(req => [
      req.cd_request,
      req.customer_name,
      req.customer_email,
      req.scan_length,
      req.margin_per_side,
      req.inc,
      req.mls,
      req.pattern_length,
      req.cd_count,
      req.mls_cpr,
      req.start_position,
      req.end_position,
      req.cd_request_status,
      req.cd_request_status !== 'New' ? formatApproverName(req.last_updated_by) : '',
      req.rejection_reason || '',
      new Date(req.creation_date).toLocaleString(),
      req.created_by
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${sanitizeCsvCell(cell)}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CodeDisk_Report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadCoordinates = async (cdRequest) => {
    try {
      const response = await fetch(
        `/api/coordinates/${cdRequest}/export?userEmail=${encodeURIComponent(user.email)}`
      );
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Error downloading coordinates');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CodeDisk_Coordinates_${cdRequest}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading coordinates:', error);
      alert('Error downloading coordinates');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'New': 'status-new',
      'Closed': 'status-closed',
      'Rejected': 'status-rejected'
    };
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };
  
  const formatApproverName = (email) => {
    if (!email) return '—';
    const localPart = email.split('@')[0];
    return localPart
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <>
    <div className="reports-page">
      <div className="page-header">
        <h2>Reports & History</h2>
        <p>View and export CodeDisk requests</p>
      </div>

      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="New">New</option>
              <option value="Closed">Closed</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          {isAdmin && (
            <div className="filter-group">
              <label>Customer Email</label>
              <input
                type="text"
                name="customerEmail"
                value={filters.customerEmail}
                onChange={handleFilterChange}
                placeholder="Search by email..."
              />
            </div>
          )}

          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              name="toDate"
              value={filters.toDate}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="btn-clear">
            Clear Filters
          </button>
          <button onClick={exportToCSV} className="btn-export">
            Export to CSV ({filteredRequests.length} records)
          </button>
        </div>
      </div>

      <div className="reports-table">
        <table>
          <thead>
            <tr>
              <th>Request #</th>
              <th>Customer</th>
              <th>Email</th>
              <th>Scan Length</th>
              <th>Margin</th>
              <th>INC</th>
              <th>MLS</th>
              <th>Count</th>
              <th>Status</th>
              <th>Approver</th>
              <th>Rejection Reason</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="13" style={{ textAlign: 'center', padding: '40px' }}>
                  No requests found
                </td>
              </tr>
            ) : (
              filteredRequests.map(request => (
                <tr key={request.cd_request}>
                  <td><strong>{request.cd_request}</strong></td>
                  <td>{request.customer_name}</td>
                  <td>{request.customer_email}</td>
                  <td>{request.scan_length} mm</td>
                  <td>{request.margin_per_side} mm</td>
                  <td>{request.inc} mm</td>
                  <td>{request.mls} mm</td>
                  <td>{request.cd_count}</td>
                  <td>{getStatusBadge(request.cd_request_status)}</td>
                  <td>{request.cd_request_status !== 'New' ? formatApproverName(request.last_updated_by) : '—'}</td>
                  <td
                    className={`rejection-reason-cell${request.rejection_reason ? ' rejection-reason-clickable' : ''}`}
                    onClick={() => request.rejection_reason && setReasonModal({ open: true, text: request.rejection_reason, requestId: request.cd_request })}
                  >
                    {request.rejection_reason || '—'}
                  </td>
                  <td>{new Date(request.creation_date).toLocaleDateString()}</td>
                  <td>
                    {request.cd_request_status === 'Closed' && (
                      <button
                        onClick={() => downloadCoordinates(request.cd_request)}
                        className="btn-download"
                      >
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="summary-section">
        <div className="summary-card">
          <div className="summary-label">Total Requests</div>
          <div className="summary-value">{filteredRequests.length}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">New</div>
          <div className="summary-value">
            {filteredRequests.filter(r => r.cd_request_status === 'New').length}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Rejected</div>
          <div className="summary-value">
            {filteredRequests.filter(r => r.cd_request_status === 'Rejected').length}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Closed</div>
          <div className="summary-value">
            {filteredRequests.filter(r => r.cd_request_status === 'Closed').length}
          </div>
        </div>
      </div>
    </div>

      {reasonModal.open && (
        <div className="reason-modal-overlay" onClick={() => setReasonModal({ open: false, text: '', requestId: null })}>
          <div className="reason-modal" onClick={e => e.stopPropagation()}>
            <div className="reason-modal-header">
              <h3>Rejection Reason — Request #{reasonModal.requestId}</h3>
              <button
                className="reason-modal-close"
                onClick={() => setReasonModal({ open: false, text: '', requestId: null })}
              >
                &times;
              </button>
            </div>
            <div className="reason-modal-body">
              {reasonModal.text}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsPage;

